import { ref } from "@vue/composition-api";
import axios from "@/libs/axios";
import { getUserData } from "@/auth/getUserData";
import { computed } from "@vue/composition-api";
import useChannels from "@/composables/v2.1/useChannels";

const users = ref([]);
const teams = ref([]);

export default function useUsers() {
    const { channels } = useChannels();
    const updateUser = (userId, userData) => {
        // Looks a bit weird this, but is needed because of reactivity: https://v2.vuejs.org/v2/guide/reactivity.html?redirect=true#For-Arrays
        const userIndex = users.value.findIndex((user) => user.id === userId);
        const user = users.value[userIndex];

        const updatedUser = {
            ...user,
            ...userData,
        };

        users.value.splice(userIndex, 1, updatedUser);

        // Backend request. From the backend, a websocket event will be emitted.
        axios.patch(`/users/${getUserData().id}`, { status: userData.status, settings: [] }).catch(() => {});
    };

    const updateUserStatus = (userId, status) => {
        // parseInt, because when going offline I do get a string (Auth0)? while normally we receive a number.
        let user = users.value.find((user) => user.id === parseInt(userId));

        if (user) {
            user.status = status;
        }
    };

    const fetchUsers = (queryParams) => {
        return axios.get("/users", { params: queryParams }).then((response) => (users.value = response.data));
    };

    const fetchTeams = (queryParams) => {
        return axios.get("/teams", { params: queryParams }).then((response) => (teams.value = response.data));
    };

    const teamsOfUser = computed(() => {
        if (!teams.value) {
            return [];
        }
        return teams.value.reduce((list, team) => {
            if (team.teamUsers?.some((teamUser) => teamUser.userId === getUserData().id)) {
                list.push({
                    id: team.id,
                    users: team.teamUsers.map((teamUser) => teamUser.userId),
                });
            }
            return list;
        }, []);
    });

    const isUserPartOfTeam = (teamId) => {
        return Boolean(
            teams.value
                .find((team) => team.id === teamId)
                ?.teamUsers.find((teamUser) => teamUser.userId === getUserData().id)
        );
    };

    // We're getting a 'random' team when we don't know to which team we should assign it. This is per spec and to avoid chats disappearing from the list. (Matthijs 2023-01)
    const getRandomUserTeamForChannel = (channelId) => {
        const channel = channels.value.find((channel) => channel.id === channelId);
        if (!channel) {
            return;
        }
        return teams.value?.find(
            (team) =>
                channel.teamChannels.some((teamChannel) => teamChannel.teamId === team.id) &&
                team.teamUsers.some((teamUser) => teamUser.userId === getUserData().id)
        )?.id;
    };

    return {
        users,
        teams,
        teamsOfUser,

        // Methods
        updateUser,
        updateUserStatus,
        fetchUsers,
        fetchTeams,
        getRandomUserTeamForChannel,
        isUserPartOfTeam,
    };
}
